import { useEffect, useState, useCallback } from 'react';
import {
  Save, RefreshCw, CheckCircle, AlertCircle, Layers, Shield, User,
  Info, Loader2,
} from 'lucide-react';
import { ApeConfig, getApeConfig } from '../services/apeConfigService';

const EMPTY: ApeConfig = {
  level0Prompt: '',
  level6Prompt: '',
  generationPromptTemplate: '',
  level0Enabled: false,
  level6Enabled: false,
};

async function upsertApeConfig(config: Pick<ApeConfig, 'level0Prompt' | 'level6Prompt' | 'generationPromptTemplate'>): Promise<void> {
  const resp = await fetch('/api/ape-config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(config),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => `Errore ${resp.status}`);
    throw new Error(text || `Errore ${resp.status}`);
  }
}

const LEVEL_DESCRIPTIONS: Record<string, { label: string; desc: string; global?: boolean }> = {
  L0: {
    label: 'L0 – Prompt Globale',
    desc: 'Regole etiche e di sistema applicate a tutte le conversazioni di ogni esperienza su questa piattaforma.',
    global: true,
  },
  L1: { label: 'L1 – Identità', desc: 'Configurato per ogni esperienza (nome, città, contesto). Gestito in Esperienze.' },
  L2: { label: 'L2 – Archetipi', desc: 'Profilo psicologico Jung. Configurato in Prompt & Configurazione per ogni esperienza.' },
  L3: { label: 'L3 – Voce & Tono', desc: 'Stile linguistico e voce TTS. Configurato in Prompt & Configurazione per ogni esperienza.' },
  L4: { label: 'L4 – Strategia Dialogica', desc: 'Struttura della conversazione. Gestito automaticamente dal motore APE 1.0.' },
  L5: { label: 'L5 – Vincoli', desc: 'Limiti storici, tematici ed etici (incluso blocco figure religiose e dittatori). Configurato per esperienza e template generazione.' },
  L6: {
    label: 'L6 – Adattamento Utente',
    desc: 'Personalizza le risposte runtime in base all\'età, livello, lingua e profilo GIADA™ dell\'utente.',
    global: true,
  },
  L7: { label: 'L7 – Contesto Runtime', desc: 'Storico messaggi e dati di sessione. Iniettato automaticamente dal motore.' },
};

export default function ApeConfigPage() {
  const [config, setConfig] = useState<ApeConfig>(EMPTY);
  const [form, setForm] = useState({ level0Prompt: '', level6Prompt: '', generationPromptTemplate: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await getApeConfig();
      setConfig(c);
      setForm({
        level0Prompt: c.level0Prompt,
        level6Prompt: c.level6Prompt,
        generationPromptTemplate: c.generationPromptTemplate,
      });
    } catch {
      setError('Impossibile caricare la configurazione APE. Riprova.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      await upsertApeConfig(form);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3500);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  const isDirty =
    form.level0Prompt !== config.level0Prompt ||
    form.level6Prompt !== config.level6Prompt ||
    form.generationPromptTemplate !== config.generationPromptTemplate;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Layers size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">APE™ L0-L6</h1>
              <p className="text-text-secondary text-sm">Configurazione globale Artificial Personality Engineering</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void load()}
            className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary border border-border rounded-lg transition-colors"
            disabled={loading}
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            Aggiorna
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving || !isDirty}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm font-medium"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? 'Salvataggio…' : 'Salva configurazione'}
          </button>
        </div>
      </div>

      {/* Feedback */}
      {success && (
        <div className="flex items-center gap-2 bg-success/10 text-success border border-success/20 rounded-lg px-4 py-3 text-sm">
          <CheckCircle size={16} /> Configurazione APE salvata correttamente.
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 bg-error/10 text-error border border-error/20 rounded-lg px-4 py-3 text-sm">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Mappa livelli */}
      <div className="bg-white rounded-xl border border-border p-5">
        <h2 className="font-semibold text-text-primary mb-1 flex items-center gap-2">
          <Info size={16} className="text-text-secondary" /> Mappa Livelli APE™ v1.0
        </h2>
        <p className="text-text-secondary text-sm mb-4">
          I livelli L0 e L6 sono globali e si configurano qui. Gli altri livelli sono gestiti per singola esperienza.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(LEVEL_DESCRIPTIONS).map(([lvl, { label, desc, global: isGlobal }]) => (
            <div
              key={lvl}
              className={`rounded-lg border p-3 text-sm ${isGlobal ? 'border-primary/40 bg-primary/5' : 'border-border bg-gray-50'}`}
            >
              <div className={`font-bold mb-0.5 text-xs ${isGlobal ? 'text-primary' : 'text-text-secondary'}`}>
                {label} {isGlobal && <span className="ml-1 text-[10px] font-normal bg-primary/10 text-primary rounded px-1 py-0.5">Globale</span>}
              </div>
              <div className="text-text-secondary text-xs leading-snug">{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-text-secondary">
          <Loader2 size={24} className="animate-spin mr-3" /> Caricamento configurazione…
        </div>
      ) : (
        <div className="space-y-5">
          {/* L0 */}
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                <Shield size={17} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary flex items-center gap-2">
                  Prompt Globale (L0)
                  {config.level0Enabled
                    ? <span className="text-[11px] bg-success/10 text-success border border-success/20 rounded px-2 py-0.5 font-normal">Attivo</span>
                    : <span className="text-[11px] bg-gray-100 text-text-secondary rounded px-2 py-0.5 font-normal">Non configurato</span>}
                </h3>
                <p className="text-text-secondary text-sm">
                  Regole etiche e istruzioni di sistema iniettate in <strong>tutte</strong> le conversazioni di ogni esperienza.
                  Definisce vincoli globali, tono generale e comportamenti della piattaforma.
                </p>
              </div>
            </div>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm font-mono resize-y min-h-[160px] focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Es: Sei un assistente culturale della piattaforma Glooci. Devi sempre rispettare le regole di sicurezza e non fornire contenuti inappropriati per nessun contesto…"
              value={form.level0Prompt}
              onChange={e => setForm(f => ({ ...f, level0Prompt: e.target.value }))}
            />
            <p className="text-text-secondary text-xs mt-1">{form.level0Prompt.length} / 3900 caratteri</p>
          </div>

          {/* L6 */}
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                <User size={17} className="text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary flex items-center gap-2">
                  Adattamento Utente (L6)
                  {config.level6Enabled
                    ? <span className="text-[11px] bg-success/10 text-success border border-success/20 rounded px-2 py-0.5 font-normal">Attivo</span>
                    : <span className="text-[11px] bg-gray-100 text-text-secondary rounded px-2 py-0.5 font-normal">Fallback default</span>}
                </h3>
                <p className="text-text-secondary text-sm">
                  Template di adattamento runtime in base al profilo utente. Supporta i placeholder:{' '}
                  <code className="bg-gray-100 rounded px-1 text-xs">{'{età}'}</code>,{' '}
                  <code className="bg-gray-100 rounded px-1 text-xs">{'{livello}'}</code>,{' '}
                  <code className="bg-gray-100 rounded px-1 text-xs">{'{lingua}'}</code>,{' '}
                  <code className="bg-gray-100 rounded px-1 text-xs">{'{adhd}'}</code>,{' '}
                  <code className="bg-gray-100 rounded px-1 text-xs">{'{sesso}'}</code>,{' '}
                  <code className="bg-gray-100 rounded px-1 text-xs">{'{nome_utente}'}</code>.
                </p>
              </div>
            </div>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm font-mono resize-y min-h-[160px] focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Es: L'utente {nome_utente} ha {età} anni, livello di conoscenza {livello}, parla {lingua}. Adatta il tuo stile narrativo di conseguenza…"
              value={form.level6Prompt}
              onChange={e => setForm(f => ({ ...f, level6Prompt: e.target.value }))}
            />
            <p className="text-text-secondary text-xs mt-1">{form.level6Prompt.length} / 3900 caratteri</p>
          </div>

          {/* Generation Template */}
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                <Layers size={17} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary">Template Generazione Personaggio</h3>
                <p className="text-text-secondary text-sm">
                  Prompt template usato dall'IA per generare automaticamente le caratteristiche di un nuovo Genius Loci da un luogo.
                  Include anche vincoli L5 per evitare figure religiose e personaggi negativi/dittatori.
                </p>
              </div>
            </div>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm font-mono resize-y min-h-[140px] focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Stai preparando i contenuti per un nuovo personaggio storico chiamato &quot;{characterName}&quot;..."
              value={form.generationPromptTemplate}
              onChange={e => setForm(f => ({ ...f, generationPromptTemplate: e.target.value }))}
            />
            <p className="text-text-secondary text-xs mt-1">{form.generationPromptTemplate.length} / 3900 caratteri</p>
          </div>

          {isDirty && (
            <div className="flex justify-end">
              <button
                onClick={() => void handleSave()}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 font-medium"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {saving ? 'Salvataggio in corso…' : 'Salva configurazione APE™'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
